<template>
  <Head :title="`${booking.name} — Admin`" />

  <div class="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black text-white">

    <!-- Animated background -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-fuchsia-600/20 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
      <div class="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-cyan-600/20 to-transparent rounded-full blur-3xl animate-pulse-slow-delayed"></div>
    </div>

    <!-- Nav -->
    <nav class="relative z-10 flex items-center justify-between px-4 md:px-10 py-5 border-b border-white/10 backdrop-blur-sm bg-black/20">
      <Link href="/" class="text-xl font-black tracking-tighter bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
        CLAUH.DJ
      </Link>
      <div class="flex items-center gap-3">
        <span class="hidden sm:block text-white/40 text-xs uppercase tracking-widest">Admin</span>
        <Link href="/" class="px-3 py-1.5 text-xs border border-white/20 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white">
          ← Site
        </Link>
        <a href="/session" data-turbo-method="delete" class="px-3 py-1.5 text-xs border border-white/10 rounded-lg hover:bg-white/5 transition-colors text-white/40 hover:text-white/70">
          Sair
        </a>
      </div>
    </nav>

    <div class="relative z-10 max-w-2xl mx-auto px-4 md:px-8 py-8 md:py-12">

      <!-- Breadcrumb + status -->
      <div class="flex items-center justify-between mb-8">
        <div class="flex items-center gap-2 text-sm">
          <Link href="/admin/bookings" class="text-white/40 hover:text-fuchsia-400 transition-colors">
            Bookings
          </Link>
          <span class="text-white/20">/</span>
          <span class="text-white/70 truncate max-w-[180px]">{{ booking.name }}</span>
        </div>
        <span :class="statusBadge">{{ statusLabel }}</span>
      </div>

      <!-- Flash -->
      <div v-if="flash.notice" class="mb-6 p-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-sm">
        {{ flash.notice }}
      </div>
      <div v-if="flash.alert" class="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
        {{ flash.alert }}
      </div>

      <!-- Main card -->
      <div class="rounded-2xl bg-white/5 border border-white/10 overflow-hidden mb-6">

        <!-- Event date hero -->
        <div class="px-6 py-5 bg-gradient-to-r from-fuchsia-600/10 to-cyan-600/10 border-b border-white/10">
          <p class="text-white/40 text-xs uppercase tracking-widest mb-1">Data do evento</p>
          <p class="text-2xl font-bold text-fuchsia-400">{{ formatDate(booking.event_date) }}</p>
          <p class="text-white/50 text-sm mt-0.5">{{ booking.event_location }}</p>
        </div>

        <!-- Details grid -->
        <div class="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
          <div class="p-6 space-y-5">
            <div>
              <p class="text-white/40 text-xs uppercase tracking-widest mb-1">Nome</p>
              <p class="font-medium">{{ booking.name }}</p>
            </div>
            <div>
              <p class="text-white/40 text-xs uppercase tracking-widest mb-1">Email</p>
              <a :href="`mailto:${booking.email}`" class="text-cyan-400 hover:underline text-sm">
                {{ booking.email }}
              </a>
            </div>
            <div>
              <p class="text-white/40 text-xs uppercase tracking-widest mb-1">Telefone</p>
              <a
                v-if="booking.phone"
                :href="`https://wa.me/${booking.phone.replace(/\D/g,'')}`"
                target="_blank"
                class="text-green-400 hover:underline text-sm flex items-center gap-1.5"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {{ booking.phone }}
              </a>
              <p v-else class="text-white/30 text-sm">Não informado</p>
            </div>
          </div>

          <div class="p-6 space-y-5">
            <div>
              <p class="text-white/40 text-xs uppercase tracking-widest mb-1">Local</p>
              <p class="text-sm">{{ booking.event_location }}</p>
            </div>
            <div>
              <p class="text-white/40 text-xs uppercase tracking-widest mb-1">Status</p>
              <span :class="statusBadge">{{ statusLabel }}</span>
            </div>
            <div>
              <p class="text-white/40 text-xs uppercase tracking-widest mb-1">Solicitado em</p>
              <p class="text-white/50 text-sm">{{ formatDate(booking.created_at) }}</p>
            </div>
          </div>
        </div>

        <!-- Message -->
        <div v-if="booking.message" class="px-6 py-5 border-t border-white/10">
          <p class="text-white/40 text-xs uppercase tracking-widest mb-2">Mensagem</p>
          <p class="text-white/70 text-sm leading-relaxed whitespace-pre-wrap">{{ booking.message }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-wrap gap-3">
        <template v-if="booking.status === 'pending'">
          <button
            @click="updateStatus('approved')"
            class="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-green-600/25"
          >
            Aprovar
          </button>
          <button
            @click="updateStatus('rejected')"
            class="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02]"
          >
            Rejeitar
          </button>
        </template>

        <button
          v-if="booking.status === 'approved'"
          @click="showCancelModal = true"
          class="flex-1 sm:flex-none px-6 py-3 border border-orange-400/30 text-orange-400 hover:bg-orange-500/10 rounded-xl font-semibold text-sm transition-all"
        >
          Cancelar booking
        </button>

        <button
          v-if="booking.status === 'rejected' || booking.status === 'cancelled'"
          @click="showDeleteModal = true"
          class="px-6 py-3 border border-red-400/30 text-red-400 hover:bg-red-500/10 rounded-xl font-semibold text-sm transition-all"
        >
          Excluir
        </button>

        <Link
          href="/admin/bookings"
          class="ml-auto px-6 py-3 border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 rounded-xl text-sm transition-all"
        >
          ← Voltar
        </Link>
      </div>

    </div>
  </div>

  <!-- Cancel modal -->
  <Transition name="modal">
    <div v-if="showCancelModal" class="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="showCancelModal = false"></div>
      <div class="relative w-full max-w-md rounded-2xl bg-gradient-to-br from-purple-950 to-black border border-white/10 p-8 shadow-2xl">
        <div class="mb-1 text-orange-400 text-xs uppercase tracking-widest font-semibold">Atenção</div>
        <h2 class="text-xl font-bold text-white mb-2">Cancelar este evento?</h2>
        <p class="text-white/50 text-sm leading-relaxed mb-8">
          Tem certeza que deseja cancelar o booking de
          <span class="text-white font-medium">{{ booking.name }}</span>
          em <span class="text-white font-medium">{{ formatDate(booking.event_date) }}</span>?
          Esta ação não pode ser desfeita.
        </p>
        <div class="flex gap-3">
          <button
            @click="showCancelModal = false"
            class="flex-1 px-5 py-3 border border-white/10 text-white/50 hover:text-white hover:border-white/20 rounded-xl text-sm font-medium transition-all"
          >
            Voltar
          </button>
          <button
            @click="confirmCancel"
            class="flex-1 px-5 py-3 bg-orange-500 hover:bg-orange-400 rounded-xl text-sm font-semibold transition-all"
          >
            Sim, cancelar
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Delete modal -->
  <Transition name="modal">
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="showDeleteModal = false"></div>
      <div class="relative w-full max-w-md rounded-2xl bg-gradient-to-br from-purple-950 to-black border border-white/10 p-8 shadow-2xl">
        <div class="mb-1 text-red-400 text-xs uppercase tracking-widest font-semibold">Atenção</div>
        <h2 class="text-xl font-bold text-white mb-2">Excluir este booking?</h2>
        <p class="text-white/50 text-sm leading-relaxed mb-8">
          Tem certeza que deseja excluir permanentemente o booking de
          <span class="text-white font-medium">{{ booking.name }}</span>?
          Ele não poderá ser recuperado.
        </p>
        <div class="flex gap-3">
          <button
            @click="showDeleteModal = false"
            class="flex-1 px-5 py-3 border border-white/10 text-white/50 hover:text-white hover:border-white/20 rounded-xl text-sm font-medium transition-all"
          >
            Voltar
          </button>
          <button
            @click="confirmDelete"
            class="flex-1 px-5 py-3 bg-red-600 hover:bg-red-500 rounded-xl text-sm font-semibold transition-all"
          >
            Sim, excluir
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Head, Link, router } from '@inertiajs/vue3'

interface Booking {
  id: number
  name: string
  email: string
  phone: string | null
  event_location: string
  event_date: string | null
  message: string | null
  status: string
  created_at: string
}

const props = defineProps<{
  booking: Booking
  flash: { notice?: string; alert?: string }
}>()

const showCancelModal = ref(false)
const showDeleteModal = ref(false)

const statusLabel = computed(() => ({
  pending: 'Pendente',
  approved: 'Confirmado',
  rejected: 'Rejeitado',
  cancelled: 'Cancelado',
} as Record<string, string>)[props.booking.status] ?? props.booking.status)

const statusBadge = computed(() => {
  const base = 'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border'
  return ({
    pending: `${base} bg-yellow-500/20 text-yellow-400 border-yellow-500/30`,
    approved: `${base} bg-green-500/20 text-green-400 border-green-500/30`,
    rejected: `${base} bg-red-500/20 text-red-400 border-red-500/30`,
    cancelled: `${base} bg-white/10 text-white/40 border-white/20`,
  } as Record<string, string>)[props.booking.status] ?? base
})

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Não informado'
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

function updateStatus(status: string) {
  router.patch(`/admin/bookings/${props.booking.id}`, { booking: { status } })
}

function confirmCancel() {
  showCancelModal.value = false
  updateStatus('cancelled')
}

function confirmDelete() {
  showDeleteModal.value = false
  router.delete(`/admin/bookings/${props.booking.id}`)
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
